import * as React from "react";
import axios from "axios";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./alert-dialog";
import { Button } from "./button";
import { Text } from "./typography";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteClosedPositionDialog - Confirmation dialog for deleting a closed position
 *
 * Uses AlertDialog pattern which prevents accidental closure by
 * clicking outside the dialog. Requires explicit cancel or confirm action.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when position is deleted successfully
 * @param {Object} props.position - The position data to delete
 */
export function DeleteClosedPositionDialog({
  open,
  onOpenChange,
  onSuccess,
  position,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog closes
  React.useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    const transactionId = position?.transactionId;
    if (!transactionId) {
      setError("No transaction ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/transactions/closed-positions/${transactionId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess(transactionId);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete closed position:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Determine display name based on position type
  const getPositionName = () => {
    if (!position) return "this position";

    if (position.statementType === "ANNUAL") {
      const year = position.date ? new Date(position.date).getFullYear() : "";
      return position.platform
        ? `${position.platform} ${year} Annual Statement`
        : `Annual Statement ${year}`;
    }

    return position.ticker || position.assetName || "this position";
  };

  const positionName = getPositionName();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle>Delete Closed Position</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            Are you sure you want to delete <strong>{positionName}</strong>?
            This action cannot be undone and all data associated with this
            realized gain/loss record will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Position"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteClosedPositionDialog;
